/**
 * Data Manager for save/load functionality and persistence
 */

import type { TestResults, UIState, AppConfig } from '../types';
import { Logger } from '../core/logger';
import {
    CURRENT_SCHEMA_VERSION,
    migrateSaveData,
    validateSaveData,
    type SaveDataCurrent,
    type SaveUiState,
} from './save-schema';
const logger = Logger.createComponentLogger('DataManager');

/** UI-state input accepted by the save path (typed UI state plus persisted extras). */
type SaveUiStateInput = Partial<UIState> & Partial<SaveUiState>;

class DataManager {
    isNodeEnv: boolean;

    constructor() {
        this.isNodeEnv = typeof window === 'undefined';
    }

    /**
     * Log notification message (no direct UI dependencies in data layer)
     * @param message - Message to log
     * @param type - 'success' | 'error'
     */
    private logMessage(message: string, type: 'success' | 'error'): void {
        if (type === 'success') {
            logger.info(message);
        } else {
            logger.error(message);
        }
    }

    /**
     * Save configuration to JSON file
     * @param {Object} testResults - Test results data
     * @param {Object} uiState - UI state (noise volume, contour, etc.)
     * @param {Object} appConfig - Application configuration
     */
    saveConfiguration(testResults: TestResults, uiState: SaveUiStateInput, appConfig: AppConfig): SaveDataCurrent | undefined {
        if (this.isNodeEnv) {
            // For Node.js testing, return the data (always return to satisfy the typed contract)
            return this._createSaveData(testResults, uiState, appConfig);
        }

        const prefix = prompt("Enter a prefix for the filename:", "eq_settings");
        if (prefix === null) { // User canceled the prompt
            return undefined;
        }
        
        const date = new Date();
        const timestamp = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}_${date.getHours().toString().padStart(2, '0')}${date.getMinutes().toString().padStart(2, '0')}`;
        const filename = `${prefix}_${timestamp}.json`;

        const dataToSave = this._createSaveData(testResults, uiState, appConfig);

        const blob = new Blob([JSON.stringify(dataToSave, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        this.logMessage('Configuration saved successfully!', 'success');

        // Return the saved data for testability (keeps behavior similar to Node path)
        return dataToSave;
    }

    /**
     * Load configuration from file
     * @param {Event} event - File input change event
     * @param {(data:any) => void} callback - Callback for successful load
     * @param {(err:any) => void} [onError] - Optional error callback
     */
    loadConfiguration(event: Event, callback: (data: unknown) => void, onError?: (err: unknown) => void): void {
        // Cast target safely; TODO: TECHNICAL DEBT - callers should pass a properly typed HTMLInputElement event
        const input = (event.target as HTMLInputElement | null);
        const file = input?.files?.[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsed = JSON.parse(e.target?.result as string);
                const migrated = migrateSaveData(parsed);
                if (migrated.error) {
                    if (onError) onError(migrated.error);
                    else if (!this.isNodeEnv) {
                        this.logMessage(migrated.error, 'error');
                    }
                } else if (migrated.data && validateSaveData(migrated.data)) {
                    if (callback) callback(migrated.data);
                    if (!this.isNodeEnv) {
                        this.logMessage('Configuration loaded successfully!', 'success');
                    }
                } else {
                    const error = 'Invalid configuration file format.';
                    if (onError) onError(error);
                    else if (!this.isNodeEnv) {
                        this.logMessage(error, 'error');
                    }
                }
            } catch (err) {
                logger.error('Error parsing configuration file:', err);
                const errorMsg = 'Could not load configuration. The file may be corrupt.';
                if (onError) onError(errorMsg);
                else if (!this.isNodeEnv) {
                    this.logMessage(errorMsg, 'error');
                }
            }
        };
        reader.readAsText(file);
    }

    /**
     * Load configuration from JSON string (for testing)
     * @param {string} jsonString - JSON configuration string
     * @returns {Object|null} Parsed configuration or null if invalid
     */
    loadConfigurationFromString(jsonString: string): SaveDataCurrent | null {
        try {
            const parsed = JSON.parse(jsonString);
            const migrated = migrateSaveData(parsed);
            if (migrated.data && validateSaveData(migrated.data)) {
                return migrated.data;
            }
            return null;
            } catch (error) {
                logger.error('Error parsing configuration string:', error);
                return null;
            }
    }

    /**
     * Reset test results
     * @param {Object} testResults - Test results object to reset
     * @param {Function} onConfirm - Callback after user confirms reset
     */
    resetResults(testResults: TestResults, onConfirm?: () => void): void {
        if (this.isNodeEnv) {
            // For testing, just reset
            testResults.both = [];
            testResults.left = [];
            testResults.right = [];
            this.removeFromLocalStorage('hearingTestResults');
            if (onConfirm) onConfirm();
            return;
        }

        // Use simple confirmation for data layer operations
        if (confirm('Are you sure you want to reset all test data? This cannot be undone.')) {
            testResults.both = [];
            testResults.left = [];
            testResults.right = [];
            this.removeFromLocalStorage('hearingTestResults');
            if (onConfirm) onConfirm();
            
            // Use notification system if available
            this.logMessage('Results have been reset successfully.', 'success');
        }
    }

    /**
     * Save to localStorage
     * @param {string} key - Storage key
     * @param {*} data - Data to save
     */
    saveToLocalStorage(key: string, data: unknown): void {
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem(key, JSON.stringify(data));
            } catch (error) {
                logger.error('Error saving to localStorage:', error);
            }
        }
    }

    /**
     * Load from localStorage
     * @param {string} key - Storage key
     * @param {*} defaultValue - Default value if key not found
     * @returns {*} Loaded value or default
     */
    loadFromLocalStorage<T = unknown>(key: string, defaultValue?: T): T | undefined {
        if (typeof localStorage !== 'undefined') {
            try {
                const item = localStorage.getItem(key);
                return item ? (JSON.parse(item) as unknown as T) : defaultValue;
            } catch (error) {
                logger.error('Error loading from localStorage:', error);
                return defaultValue;
            }
        }
        return defaultValue;
    }

    /**
     * Remove from localStorage
     * @param {string} key - Storage key
     */
    removeFromLocalStorage(key: string): void {
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                logger.error('Error removing from localStorage:', error);
            }
        }
    }

    /**
     * Create save data object
     * @private
     */
    _createSaveData(testResults: TestResults, uiState: SaveUiStateInput, appConfig: AppConfig): SaveDataCurrent {
        return {
            schemaVersion: CURRENT_SCHEMA_VERSION,
            savedAt: new Date().toISOString(),
            testResults,
            uiState: {
                noiseVolume: uiState.noiseVolume,
                contour: uiState.contour,
                sinePlaybackMode: uiState.sinePlaybackMode,
                volumeDbStep: uiState.volumeDbStep,
            },
            appConfig: {
                resultUsageMinFrequency: appConfig.resultUsageMinFrequency,
                resultUsageMaxFrequency: appConfig.maxFreq,
            },
        };
    }
}

// ES module export
export { DataManager };
export default DataManager;
